import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog, AppBar, Toolbar, IconButton, Typography, Box, Button,
  Container, Card, CardContent, Stack, TextField, FormControl,
  Select, MenuItem, CircularProgress, Snackbar, Alert, Chip,
  Divider, InputAdornment, FormHelperText
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Slide } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

// Import assetService directly into the modal
import { assetService } from '../../services/assetService';

// ─────────────────────────────────────────────────────────────────────────────
// SLIDE TRANSITION
// ─────────────────────────────────────────────────────────────────────────────
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ─────────────────────────────────────────────────────────────────────────────
// STYLED COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
const SectionCard = styled(Card)(() => ({
  borderRadius: 8,
  border: '1px solid #e2e8f0',
}));

const SectionTitle = styled(Typography)(() => ({
  fontSize: '0.9375rem', 
  fontWeight: 700, 
  color: '#1e293b',
  marginBottom: '1.25rem', 
  paddingBottom: '0.75rem',
  borderBottom: '1px solid #e5e7eb',
}));

const FieldLabel = styled(Typography)(() => ({
  fontSize: '0.8125rem', 
  fontWeight: 600, 
  color: '#374151',
  textTransform: 'uppercase', 
  letterSpacing: '0.025em',
  marginBottom: '0.375rem',
}));

const FormGroup = styled(Box)(() => ({
  flex: 1, 
  minWidth: '200px', 
  display: 'flex', 
  flexDirection: 'column', 
  gap: '0.375rem',
  '& .MuiOutlinedInput-root': {
    background: '#fff',
    '& fieldset': { borderColor: '#d1d5db' },
    '&:hover fieldset': { borderColor: '#3b82f6' },
    '&.Mui-focused fieldset': {
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 3px rgba(59,130,246,0.12)',
    },
  },
}));

const FormRow = styled(Box)(() => ({
  display: 'flex', 
  gap: '1rem', 
  marginBottom: '1rem', 
  flexWrap: 'wrap',
  '&:last-child': { marginBottom: 0 },
}));

const cancelBtnSx = {
  px: 3, py: 1, backgroundColor: '#f3f4f6', color: '#374151',
  border: '1px solid #d1d5db', borderRadius: '6px', fontWeight: 500,
  textTransform: 'none', minWidth: '100px', height: '40px',
  '&:hover': { backgroundColor: '#e5e7eb' },
  '&:disabled': { opacity: 0.6 },
};

const submitBtnSx = {
  px: 3, py: 1, backgroundColor: '#2563eb', color: '#fff',
  borderRadius: '6px', fontWeight: 600, textTransform: 'none',
  minWidth: '160px', height: '40px',
  display: 'flex', alignItems: 'center', gap: 1,
  '&:hover': { backgroundColor: '#1d4ed8' },
  '&:disabled': { backgroundColor: '#93c5fd' },
};

const helperErrorSx = {
  color: '#dc2626',
  fontSize: '0.75rem',
  fontWeight: 500,
  margin: 0,
  marginTop: '4px',
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const QuantityAdjustmentModal = ({ 
  isOpen, 
  onClose, 
  asset, 
  onSave,       // Standardized Callback on success
  onError,      // Standardized Callback on failure
  currentUser 
}) => {
  const [adjustmentType, setAdjustmentType] = useState('increase');
  const [quantity, setQuantity] = useState(1);
  const [purchaseBillNumber, setPurchaseBillNumber] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [reason, setReason] = useState('');
  const [billFile, setBillFile] = useState(null);
  const [billFileBase64, setBillFileBase64] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  const fileInputRef = useRef(null);
  const formBodyRef = useRef(null);

  // Initialize and clean form state on opening
  useEffect(() => {
    if (isOpen) {
      setAdjustmentType('increase');
      setQuantity(1);
      setPurchaseBillNumber('');
      setPurchaseDate('');
      setReason('');
      setBillFile(null);
      setBillFileBase64('');
      setErrors({});
      setSubmitError('');
    }
  }, [isOpen]);

  if (!isOpen || !asset) return null;

  const currentQuantity = asset.quantity || 0;
  const targetQuantity = adjustmentType === 'increase' 
    ? currentQuantity + quantity 
    : Math.max(0, currentQuantity - quantity);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        setSubmitError('Please select a PDF or image file (JPG, PNG, PDF)');
        return;
      }
      if (file.size > 700 * 1024) {
        setSubmitError('File size must be less than 700KB');
        return;
      }
      
      setSubmitError('');
      setBillFile(file);
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setBillFileBase64(event.target.result);
      };
      reader.onerror = () => {
        setSubmitError('Error reading file. Please try again.');
        setBillFile(null);
        setBillFileBase64('');
      };
      reader.readAsDataURL(file);
    }
  };

  const removeBillFile = () => {
    setBillFile(null);
    setBillFileBase64('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validate = () => {
    const errs = {};
    if (quantity <= 0) {
      errs.quantity = 'Quantity must be greater than 0';
    }

    if (adjustmentType === 'increase') {
      if (!purchaseBillNumber.trim()) {
        errs.purchaseBillNumber = 'Purchase bill number is required';
      }
      if (!purchaseDate) {
        errs.purchaseDate = 'Purchase date is required';
      }
    }

    if (adjustmentType === 'decrease') {
      if (quantity > currentQuantity) {
        errs.quantity = `Cannot decrease quantity below current stock (${currentQuantity})`;
      }
    }

    if (!reason.trim()) {
      errs.reason = 'Reason for adjustment is required';
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!validate()) return;

    if (adjustmentType === 'decrease' && currentQuantity - quantity === 0) {
      const proceed = window.confirm(
        'Warning: This adjustment will reduce quantity to 0, making the asset completely unavailable. Do you wish to proceed?'
      );
      if (!proceed) return;
    }

    try {
      setLoading(true);
      setSubmitError('');

      // Invoke the adjustment directly via assetService
      const updatedAsset = await assetService.adjustQuantity(
        asset.id,
        {
          adjustmentType,
          quantity,
          reason: reason.trim(),
          purchaseBillNumber: adjustmentType === 'increase' ? purchaseBillNumber.trim() : null,
          purchaseDate: adjustmentType === 'increase' && purchaseDate ? new Date(purchaseDate).toISOString() : null,
          billFileUrl: adjustmentType === 'increase' ? (billFileBase64 || null) : null,
          billFileName: adjustmentType === 'increase' ? (billFile?.name || null) : null,
        },
        currentUser?.email
      );

      if (onSave) {
        onSave(updatedAsset);
      }
      onClose();
    } catch (error) {
      console.error('Quantity update failed:', error);
      const errMsg = error.message || 'Failed to update asset quantity.';
      setSubmitError(errMsg);
      if (onError) {
        onError(errMsg, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      fullScreen
      open={isOpen}
      onClose={!loading ? onClose : undefined}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          width: '100vw', height: '100vh',
          maxWidth: '100vw', maxHeight: '100vh',
          margin: 0, boxSizing: 'border-box',
        },
      }}
    >
      {/* ── APP BAR ──────────────────────────────────────────────────────── */}
      <AppBar sx={{ position: 'relative', flexShrink: 0 }} elevation={1}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose} disabled={loading}>
            <CloseIcon />
          </IconButton>

          <Box sx={{ ml: 2, flex: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap sx={{ lineHeight: 1.2 }}>
              Adjust Asset Quantity
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }} noWrap>
              {asset.assetId} — {asset.assetName}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* ── SCROLLABLE BODY ───────────────────────────────────────────────── */}
      <Box
        ref={formBodyRef}
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto', bgcolor: '#f1f5f9', py: 4 }}
      >
        <Container maxWidth="md">
          <Stack spacing={3}>
            
            {/* ── CURRENT BALANCE SUMMARY CARD ─────────────────────────────── */}
            <SectionCard variant="outlined">
              <CardContent sx={{ p: 3 }}>
                <SectionTitle>Current Inventory Balance</SectionTitle>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  
                  {/* Current Quantity */}
                  <Box sx={{
                    flex: 1, minWidth: 220, p: 2, borderRadius: 2,
                    background: '#eff6ff', border: '1px solid #bfdbfe',
                  }}>
                    <Typography sx={{
                      fontSize: '0.75rem', fontWeight: 600, color: '#6b7280',
                      textTransform: 'uppercase', mb: 0.5,
                    }}>
                      In Stock Quantity
                    </Typography>
                    <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e40af' }}>
                      {currentQuantity} <span style={{ fontSize: '1rem', fontWeight: 500, color: '#6b7280' }}>
                        {asset.unitOfMeasurement || 'units'}
                      </span>
                    </Typography>
                  </Box>

                  {/* Calculated Balance Preview */}
                  <Box sx={{
                    flex: 1, minWidth: 220, p: 2, borderRadius: 2,
                    background: adjustmentType === 'increase' ? '#f0fdf4' : '#fff5f5', 
                    border: `1px solid ${adjustmentType === 'increase' ? '#bbf7d0' : '#feb2b2'}`,
                  }}>
                    <Typography sx={{
                      fontSize: '0.75rem', fontWeight: 600, color: '#6b7280',
                      textTransform: 'uppercase', mb: 0.5,
                    }}>
                      Calculated Balance after Adjustment
                    </Typography>
                    <Typography sx={{ 
                      fontSize: '1.5rem', fontWeight: 700, 
                      color: adjustmentType === 'increase' ? '#166534' : '#c53030' 
                    }}>
                      {targetQuantity} <span style={{ fontSize: '1rem', fontWeight: 500, color: '#6b7280' }}>
                        {asset.unitOfMeasurement || 'units'}
                      </span>
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </SectionCard>

            {/* ── ADJUSTMENT CONTROLS CARD ─────────────────────────────────── */}
            <SectionCard variant="outlined">
              <CardContent sx={{ p: 3 }}>
                <SectionTitle>Adjustment Controls</SectionTitle>

                <FormRow>
                  <FormGroup>
                    <FieldLabel>Adjustment Action Type</FieldLabel>
                    <FormControl fullWidth size="small">
                      <Select
                        value={adjustmentType}
                        onChange={(e) => setAdjustmentType(e.target.value)}
                        disabled={loading}
                      >
                        <MenuItem value="increase">Increase Quantity (Stock In)</MenuItem>
                        <MenuItem value="decrease">Decrease Quantity (Stock Out)</MenuItem>
                      </Select>
                    </FormControl>
                  </FormGroup>

                  <FormGroup>
                    <FieldLabel>Quantity count to adjust</FieldLabel>
                    <TextField
                      id="quantity"
                      name="quantity"
                      type="number"
                      size="small"
                      fullWidth
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
                      inputProps={{ min: 1 }}
                      disabled={loading}
                      error={Boolean(errors.quantity)}
                      helperText={errors.quantity || ""}
                      FormHelperTextProps={{ sx: helperErrorSx }}
                    />
                  </FormGroup>
                </FormRow>

                {/* Conditional Increase Procurement Info */}
                {adjustmentType === 'increase' && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <FormRow>
                      <FormGroup>
                        <FieldLabel>Purchase Bill Number</FieldLabel>
                        <TextField
                          id="purchaseBillNumber"
                          name="purchaseBillNumber"
                          size="small"
                          fullWidth
                          placeholder="Bill or invoice identifier"
                          value={purchaseBillNumber}
                          onChange={(e) => setPurchaseBillNumber(e.target.value)}
                          disabled={loading}
                          error={Boolean(errors.purchaseBillNumber)}
                          helperText={errors.purchaseBillNumber || ""}
                          FormHelperTextProps={{ sx: helperErrorSx }}
                        />
                      </FormGroup>

                      <FormGroup>
                        <FieldLabel>Purchase Date</FieldLabel>
                        <TextField
                          id="purchaseDate"
                          name="purchaseDate"
                          type="date"
                          size="small"
                          fullWidth
                          value={purchaseDate}
                          onChange={(e) => setPurchaseDate(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ max: new Date().toISOString().split('T')[0] }}
                          disabled={loading}
                          error={Boolean(errors.purchaseDate)}
                          helperText={errors.purchaseDate || ""}
                          FormHelperTextProps={{ sx: helperErrorSx }}
                        />
                      </FormGroup>
                    </FormRow>

                    <FormRow>
                      <FormGroup>
                        <FieldLabel>Upload Bill/Invoice Attachment</FieldLabel>
                        <input
                          ref={fileInputRef}
                          id="billFile"
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleFileChange}
                          style={{ display: 'none' }}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Button
                            type="button"
                            variant="outlined"
                            size="small"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={loading}
                            startIcon={<AttachFileIcon />}
                            sx={{
                              flex: 1, textTransform: 'none', fontWeight: 500,
                              borderStyle: 'dashed', borderColor: '#d1d5db',
                              color: '#374151', bgcolor: '#f9fafb', py: '0.45rem',
                              '&:hover': { bgcolor: '#f3f4f6', borderColor: '#9ca3af' },
                            }}
                          >
                            {billFile ? 'Change File' : 'Choose File'}
                          </Button>
                          {billFile && (
                            <Button
                              type="button"
                              onClick={removeBillFile}
                              sx={{
                                minWidth: 'auto', border: '1px solid #fca5a5',
                                color: '#dc2626', borderRadius: '4px', px: 1.5,
                                '&:hover': { background: '#fef2f2' },
                              }}
                            >
                              Clear
                            </Button>
                          )}
                        </Box>
                        
                        {/* File preview statuses */}
                        {billFile && (
                          <Box sx={{ mt: 1, p: 1, bgcolor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: '#374151', wordBreak: 'break-all' }}>
                              📎 {billFile.name} ({(billFile.size / 1024).toFixed(1)} KB)
                            </Typography>
                            {billFileBase64 ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                <CheckCircleIcon sx={{ fontSize: 14, color: '#10b981' }} />
                                <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 600 }}>File converted successfully</Typography>
                              </Box>
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                <HourglassEmptyIcon sx={{ fontSize: 14, color: '#3b82f6', animation: 'spin 1.5s linear infinite' }} />
                                <Typography variant="caption" sx={{ color: '#3b82f6' }}>Processing document data...</Typography>
                              </Box>
                            )}
                          </Box>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                          Accepted formats: PDF, JPG, PNG · Max 700KB.
                        </Typography>
                      </FormGroup>
                    </FormRow>
                  </>
                )}

                <Divider sx={{ my: 3 }} />

                <FormRow>
                  <FormGroup>
                    <FieldLabel>Reason for adjustment</FieldLabel>
                    <TextField
                      id="reason"
                      name="reason"
                      multiline
                      rows={3}
                      fullWidth
                      placeholder={
                        adjustmentType === 'increase'
                          ? "e.g., New procurement intake, return, error correction..."
                          : "e.g., Defective item discard, lost items, internal write-off..."
                      }
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      disabled={loading}
                      error={Boolean(errors.reason)}
                      helperText={errors.reason || ""}
                      FormHelperTextProps={{ sx: helperErrorSx }}
                    />
                  </FormGroup>
                </FormRow>
              </CardContent>
            </SectionCard>
          </Stack>
        </Container>
      </Box>

      {/* ── STICKY FOOTER ────────────────────────────────────────────────── */}
      <Box sx={{
        flexShrink: 0, width: '100%', height: '72px',
        borderTop: '1px solid #e2e8f0', backgroundColor: '#fff',
        boxShadow: '0 -2px 8px rgba(0,0,0,0.04)',
        display: 'flex', alignItems: 'center',
        justifyContent: 'flex-end', gap: 1.5, px: 3,
      }}>
        <Button onClick={onClose} disabled={loading} sx={cancelBtnSx}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          disabled={loading || (billFile && !billFileBase64)} 
          sx={submitBtnSx}
        >
          {loading && <CircularProgress size={16} color="inherit" />}
          {loading 
            ? 'Adjusting…' 
            : `${adjustmentType === 'increase' ? 'Add' : 'Remove'} Quantity`}
        </Button>
      </Box>

      {/* ── ERROR SNACKBAR ────────────────────────────────────────────────── */}
      <Snackbar
        open={Boolean(submitError)}
        autoHideDuration={6000}
        onClose={() => setSubmitError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 8 }}
      >
        <Alert
          onClose={() => setSubmitError('')}
          severity="error"
          variant="filled"
          icon={<ErrorOutlineIcon />}
          sx={{ width: '100%', minWidth: 400, fontSize: '0.9rem', fontWeight: 500 }}
        >
          {submitError}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default QuantityAdjustmentModal;